import { create } from 'zustand';

import { ECheckoutStatus } from '../../../constants/enums';
import { PriceBreakdown, SavedCard } from '../../../contracts';

type PromoCodeMetadata = {
  name: string;
  discountAmount: number;
  discountPercentage: number;
};

type PaymentState = {
  errorMessage: string;
  isPaymentLoading: boolean;
  isApmLoading: boolean;
  isExpressCheckoutLoading: boolean;
  isPaymentElementLoading: boolean;
  zipCode: string;
  checkoutStatus: ECheckoutStatus;
  priceBreakdown: PriceBreakdown | null;
  // need to move all geo related state into one object
  isZipCodeValidatedOnce: boolean;
  fullAddress?: string;
  customerGeoState?: string;
  promoCodeMetadata?: PromoCodeMetadata;
  savedCards: SavedCard[];
  publisherPurchaseId?: string;
  shouldSaveCard?: boolean;
  email?: string;
  // Monotonic counter incremented whenever the user requests aborting an
  // in-flight APM redirect flow (e.g. clicks "try again" on the overlay).
  // Long-running APM polling loops snapshot this value and bail out when it
  // changes, so they can stop polling immediately without waiting for the
  // popup-tab heuristics to detect a close.
  apmAbortSignal: number;
};

type PaymentAction = {
  setErrorMessage: (messege: string) => void;
  setIsPaymentLoading: (isLoading: boolean) => void;
  setIsApmLoading: (isLoading: boolean) => void;
  setIsPaymentElementLoading: (isLoading: boolean) => void;
  setIsExpressCheckoutElementLoading: (isLoading: boolean) => void;
  setCheckoutStatus: (status: ECheckoutStatus) => void;
  // need to think about extracting this logic to a different hook, maybe the same hook of Nuvei with the dynamic form
  setIsZipCodeValidatedOnce: () => void;
  setZipCode: (zip: string) => void;
  setPriceBreakdown: (priceBreakdown: PriceBreakdown) => void;
  setFullAddress: (address: string) => void;
  setCustomerGeoState: (state?: string) => void;
  setPromoCodeMetadata: (promoCodeMetadata?: PromoCodeMetadata) => void;
  setSavedCards: (savedCreditCards: SavedCard[]) => void;
  setPublisherPurchaseId: (publisherPurchaseId?: string) => void;
  setShouldSaveCard: (save: boolean) => void;
  setEmail: (email: string) => void;
  // Aborts any in-flight APM redirect flow (closes the loading state,
  // signals the polling loop in useStripeSubmit to exit cleanly).
  abortApmFlow: () => void;
};

export const usePaymentState = create<PaymentState & PaymentAction>()((set) => ({
  errorMessage: '',
  isPaymentLoading: false,
  isPaymentElementLoading: true,
  isApmLoading: false,
  isExpressCheckoutLoading: true,
  checkoutStatus: ECheckoutStatus.INIT,
  zipCode: '',
  priceBreakdown: null,
  isZipCodeValidatedOnce: false,
  fullAddress: '',
  savedCards: [],
  publisherPurchaseId: '',
  shouldSaveCard: false,
  email: '',
  apmAbortSignal: 0,
  setErrorMessage: (messege) => set({ errorMessage: messege }),
  setIsApmLoading: (isLoading) => set({ isApmLoading: isLoading }),
  setIsPaymentLoading: (isLoading) => set({ isPaymentLoading: isLoading }),
  setIsPaymentElementLoading: (isLoading) => set({ isPaymentElementLoading: isLoading }),
  setIsExpressCheckoutElementLoading: (isLoading) => set({ isExpressCheckoutLoading: isLoading }),
  setCheckoutStatus: (status) => set({ checkoutStatus: status }),
  setZipCode: (zipCode: string) => set({ zipCode }),
  triggerZipCodValidation: () => {},
  setPriceBreakdown: (priceBreakdown) => set({ priceBreakdown }),
  setIsZipCodeValidatedOnce: () => set({ isZipCodeValidatedOnce: true }),
  setFullAddress: (address: string) => set({ fullAddress: address }),
  setPromoCodeMetadata: (promoCodeMetadata) => set({ promoCodeMetadata }),
  setSavedCards: (savedCards) => set({ savedCards }),
  setCustomerGeoState: (state) => set({ customerGeoState: state }),
  setPublisherPurchaseId: (publisherPurchaseId) => set({ publisherPurchaseId }),
  setShouldSaveCard: (save) => set({ shouldSaveCard: save }),
  setEmail: (email) => set({ email }),
  abortApmFlow: () =>
    set((state) => ({
      apmAbortSignal: state.apmAbortSignal + 1,
      isPaymentLoading: false,
      errorMessage: '',
    })),
}));
